import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { formatSubscriptionDate } from 'utils/dates';
import { formatMoney } from 'utils/money';
import {
  getBillingPortalUrl,
  getCardExpiryParts,
  getLastInvoiceMeta,
  getLastInvoicePdfUrl,
  getPaymentMethodSummary,
} from 'utils/subscriptionBilling';
import {
  ContentWrapper,
  Loader,
  PopupButtonMessage,
  PopupEventWrapper,
} from 'components';
import { ROUTES, PROFILE_ROUTES } from 'configs';
import {
  selectCurrentUserSubscription,
  selectIsSubscriptionLoading,
  selectTrialStartErrorKey,
} from 'store/selectors/subscriptions';
import { getAuthUser } from 'store/selectors/auth';
import { getCommunities } from 'store/selectors/communities';
import {
  startTrial,
  activateSubscription,
  getAllSubscriptions,
  getCurrentUserSubscription,
} from 'store/actionCreators/subscriptions';
import {
  DISMISSED_TOAST_IDS,
  isAppToastDismissed,
  markAppToastDismissed,
} from 'utils/dismissedAppToasts';
import { toast as addAppToast } from 'utils/toastAbstraction';
import {
  SubscriptionFrequency,
  SubscriptionStatus,
  UserSubscription,
} from 'types';

import Lottie from 'react-lottie';
import warningSign from 'static/animations/warning-sign.json';
import { ReactComponent as RocketLaunchIcon } from 'static/images/rocket_launch.svg';

import { StripeWordmark } from 'pages/Subscriptions/SubscriptionInner/PaymentBrandIcons';
import { PaymentCardBrandIcon } from 'components';
import { parseStripePaymentMethod } from 'utils/stripePaymentMethod';

import StripeCheckoutOutcome from './StripeCheckoutOutcome';
import SwitchBillingModeLink from './SwitchBillingModeLink';

import {
  ActionRow,
  Badge,
  BarFill,
  BarTrack,
  BillingPortalPlanButton,
  BtnOutline,
  BtnPrimary,
  Card,
  CardHeaderRow,
  CardSubtitle,
  CardTitle,
  DropdownItem,
  DropdownPanel,
  FeatureBlurb,
  FeatureBox,
  FeatureItem,
  FeatureList,
  FloatingLoaderWrap,
  FooterNote,
  HeroAccent,
  HeroActions,
  HeroCard,
  HeroInner,
  HeroLeft,
  HeroSubtitle,
  HeroTitle,
  IconBox,
  InfoNote,
  Main,
  PageRoot,
  PaymentBlock,
  PaymentIconWrap,
  PaymentLabel,
  PaymentRow,
  PaymentValue,
  PlanFooter,
  PlanName,
  SmallHint,
  CheckoutOutcomeEnter,
  ChoosePlanFullButton,
  OnboardingConfirmCard,
  OnboardingConfirmHint,
  OnboardingConfirmLoaderSlot,
  OnboardingConfirmShimmer,
  OnboardingConfirmShimmerShort,
  OnboardingConfirmTitle,
  SplitButtonChevron,
  SplitButtonMain,
  SplitButtonWrap,
  StatusBanner,
  StatusBannerBody,
  StatusBannerTitle,
  TextLink,
  TopBar,
  TopBarTitle,
  TrialBanner,
  TrialBannerContent,
  TrialBannerIconWrap,
  TrialBannerText,
  TrialBannerTitle,
  FooterStripeNote,
  TrustRow,
  TwoColGrid,
  UsageCard,
  UsageFraction,
  UsageFractionMuted,
  UsageGrid,
  UsageHeader,
  UsageHint,
  UsageHintSmall,
  UsageHintWarn,
  UsageLabel,
  UsageMetric,
  UsageMetricTop,
  UsageUpgradeBtn,
  UsageUpgradeNudge,
  UsageUpgradeNudgeText,
  StatusBannerMain,
  StatusCheckWrap,
  StatusBannerTextCol,
  FrequencyPill,
  TextActionLink,
} from './styled';
import { Headline, Headline2 } from 'pages/UserProfile/Header/styled';
import { Layout } from 'pages/UserProfileCommunities/styled';

const TRIAL_DEFAULTS = {
  maxCommunities: 30,
  maxGroups: 600,
  maxMembers: 3000,
  maxStorage: 15,
} as const;

const pct = (used: number, max: number) => {
  if (!max || max <= 0) return 0;
  return Math.min(100, Math.round((used / max) * 100));
};

const IconCheck = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="#00a4a6"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
    />
  </svg>
);

const IconInfo = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="#f5a623" aria-hidden>
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
  </svg>
);

/** Same Lottie as `InfoPopup` / `PopupButtonMessage` start-trial flow (`type="warning"`) */
const TRIAL_BANNER_LOTTIE_OPTIONS = {
  loop: true,
  autoplay: true,
  animationData: warningSign,
};

const TrialBannerWarningLottie = () => (
  <TrialBannerIconWrap aria-hidden>
    <Lottie
      options={TRIAL_BANNER_LOTTIE_OPTIONS}
      width={44}
      height={44}
      isClickToPauseDisabled
    />
  </TrialBannerIconWrap>
);

const IconLock = ({ size = 14 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
  </svg>
);

const IconCalendar = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z" />
  </svg>
);

const IconOpen = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" />
  </svg>
);

const IconChevronDown = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M7 10l5 5 5-5z" />
  </svg>
);

const IconChevronRight = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden
  >
    <path d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6-6-6z" />
  </svg>
);

const UserProfileSubscription = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const subscription = useSelector(selectCurrentUserSubscription);
  const profile = useSelector(getAuthUser);
  const communities = useSelector(getCommunities);
  const isLoading = useSelector(selectIsSubscriptionLoading);
  const trialStartErrorKey = useSelector(selectTrialStartErrorKey);
  const [planMenuOpen, setPlanMenuOpen] = useState(false);
  const planMenuRef = useRef<HTMLDivElement>(null);
  /** After 10s of polling, show success outcome with fallback plan label. */
  const [checkoutSyncTimedOut, setCheckoutSyncTimedOut] = useState(false);

  const changePlanPath = `/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CHANGE_PLAN}`;
  const cancelSubscriptionPath = `/${PROFILE_ROUTES.SUBSCRIPTION}/${PROFILE_ROUTES.CANCEL_SUBSCRIPTION}`;

  const stripeOutcome = useMemo(() => {
    if (searchParams.get('stripe_success') === '1') return 'success' as const;
    if (
      searchParams.get('stripe_cancel') === '1' ||
      searchParams.get('canceled') === 'true'
    ) {
      return 'failure' as const;
    }
    return null;
  }, [searchParams]);

  const checkoutStripeFlow = useMemo(
    () =>
      searchParams.get('checkout') === 'onboarding'
        ? ('onboarding' as const)
        : ('subscription' as const),
    [searchParams],
  );

  const sessionIdFromUrl = useMemo(
    () => searchParams.get('session_id'),
    [searchParams],
  );

  const checkoutPlanIdFromUrl = useMemo(
    () => searchParams.get('checkout_plan_id'),
    [searchParams],
  );

  const checkoutFrequencyFromUrl = useMemo(
    () => searchParams.get('checkout_frequency'),
    [searchParams],
  );

  const subscriptionReturnExpectsSync = useMemo(
    () =>
      checkoutStripeFlow === 'subscription' &&
      Boolean(checkoutPlanIdFromUrl) &&
      Boolean(checkoutFrequencyFromUrl),
    [checkoutStripeFlow, checkoutPlanIdFromUrl, checkoutFrequencyFromUrl],
  );

  const subscriptionCheckoutSynced = useMemo(() => {
    if (!checkoutPlanIdFromUrl || !checkoutFrequencyFromUrl) return false;
    const freqOk =
      checkoutFrequencyFromUrl === SubscriptionFrequency.MONTH ||
      checkoutFrequencyFromUrl === SubscriptionFrequency.YEAR;
    if (!freqOk) return false;
    const sub = subscription as UserSubscription | undefined;
    if (!sub?.id) return false;
    if (sub.subscriptionPlanId !== checkoutPlanIdFromUrl) return false;
    if (sub.frequency !== checkoutFrequencyFromUrl) return false;
    if (!sub.subscriptionId?.trim()) return false;
    if (sub.status !== SubscriptionStatus.ACTIVE) return false;
    return true;
  }, [checkoutPlanIdFromUrl, checkoutFrequencyFromUrl, subscription]);

  useEffect(() => {
    if (stripeOutcome !== 'success') {
      setCheckoutSyncTimedOut(false);
    }
  }, [stripeOutcome]);

  const clearStripeQuery = useCallback(() => {
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (window) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    dispatch(getCurrentUserSubscription.trigger());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getAllSubscriptions.trigger({ silent: true }));
  }, [dispatch]);

  useEffect(() => {
    if (!planMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (
        planMenuRef.current &&
        !planMenuRef.current.contains(e.target as Node)
      ) {
        setPlanMenuOpen(false);
      }
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, [planMenuOpen]);

  const groupsCount = useMemo(() => {
    if (communities.length > 0 && profile) {
      const owned = communities.filter(
        (item) => profile?.userId === item?.communityOwner?.userId,
      );
      return owned.reduce(
        (acc, item) =>
          acc +
          Math.max(Number(item.groupsCount) || 0, item.groups?.length || 0),
        0,
      );
    }
    return 0;
  }, [communities, profile]);
  const ownedCommunities = useMemo(() => {
    if (!profile?.userId || communities.length === 0) return [];
    return communities.filter(
      (item) => profile.userId === item.communityOwner?.userId,
    );
  }, [communities, profile?.userId]);
  const ownedUsersCount = useMemo(
    () =>
      ownedCommunities.reduce(
        (acc, item) => acc + (Number(item.usersCount) || 0),
        0,
      ),
    [ownedCommunities],
  );
  const ownedCommunitiesCount = ownedCommunities.length;

  const subscriptionEndDate = useMemo(() => {
    const sub = subscription as UserSubscription | null | undefined;
    let endIso: string | null | undefined;
    if (
      sub?.stripeSubscriptionStatus &&
      ['canceled', 'unpaid', 'incomplete_expired'].includes(
        sub.stripeSubscriptionStatus,
      )
    ) {
      endIso =
        sub.stripeEndedAt ||
        sub.stripeCancelAt ||
        sub.stripeCurrentPeriodEnd ||
        undefined;
    } else if (
      sub?.stripeSubscriptionStatus === 'active' &&
      sub.stripeCancelAtPeriodEnd
    ) {
      endIso = sub.stripeCancelAt || sub.stripeCurrentPeriodEnd || undefined;
    } else if (sub?.stripeCurrentPeriodEnd) {
      endIso = sub.stripeCurrentPeriodEnd;
    } else {
      endIso = subscription?.endDate ?? undefined;
    }
    if (!endIso) return '-';
    return formatSubscriptionDate(endIso, i18n.language);
  }, [subscription, i18n.language]);

  const translatedPaymentCycle = useMemo(() => {
    if (subscription?.frequency === SubscriptionFrequency.MONTH) {
      return t('subscriptions.profile.labels.paymentCycleMonth');
    }
    if (subscription?.frequency === SubscriptionFrequency.YEAR) {
      return t('subscriptions.profile.labels.paymentCycleYear');
    }
    return '';
  }, [subscription, t]);

  const nextPaymentLabel = useMemo(() => {
    if (
      (subscription?.status === SubscriptionStatus.CANCELLED &&
        subscription?.subscriptionId !== SubscriptionStatus.MIGRATED) ||
      subscription?.planId === SubscriptionStatus.TRIAL
    ) {
      return t('subscriptions.profile.labels.validUntil');
    }
    return t('subscriptions.profile.labels.nextPayment');
  }, [subscription, t]);

  const planName = useMemo(() => {
    if (subscription?.id) {
      if (
        subscription.status === SubscriptionStatus.CANCELLED &&
        subscription.subscriptionId !== SubscriptionStatus.MIGRATED
      ) {
        return t('subscriptions.cancelledTitle');
      }
      if (subscription.planId === SubscriptionStatus.TRIAL) {
        return t('subscriptions.trial');
      }
      if (subscription.subscriptionPlan) {
        return subscription.subscriptionPlan.productName;
      }
      return subscription.planId;
    }
    return t('subscriptions.free');
  }, [subscription, t]);

  const isCancelled =
    subscription?.status === SubscriptionStatus.CANCELLED &&
    subscription?.subscriptionId !== SubscriptionStatus.MIGRATED;

  const isTrial =
    Boolean(subscription?.id) &&
    subscription?.planId === SubscriptionStatus.TRIAL;

  const isPaidActive =
    Boolean(subscription?.id) &&
    subscription?.planId !== SubscriptionStatus.TRIAL &&
    !isCancelled;

  const isStripeSubscriptionEnded = useMemo(() => {
    const sub = subscription as UserSubscription | undefined;
    if (!isPaidActive || !sub?.stripeSubscriptionStatus) return false;
    return ['canceled', 'unpaid', 'incomplete_expired'].includes(
      sub.stripeSubscriptionStatus,
    );
  }, [isPaidActive, subscription]);

  const isStripeNonRenewingActive = useMemo(() => {
    const sub = subscription as UserSubscription | undefined;
    return Boolean(
      isPaidActive &&
        sub?.stripeSubscriptionStatus === 'active' &&
        sub?.stripeCancelAtPeriodEnd === true,
    );
  }, [isPaidActive, subscription]);

  const enterpriseAccountManagerName =
    process.env.REACT_APP_ENTERPRISE_ACCOUNT_MANAGER_NAME || '';
  const enterpriseAccountManagerEmail =
    process.env.REACT_APP_ENTERPRISE_ACCOUNT_MANAGER_EMAIL || '';

  const isInvoiceBillingLayout = useMemo(() => {
    if (!isPaidActive) return false;
    const sub = subscription as UserSubscription | null | undefined;
    if (!sub) return false;
    if (sub.stripeCollectionMethod === 'send_invoice') return true;
    if (sub.enterpriseInvoiceRequest?.contactEmail) return true;
    return false;
  }, [isPaidActive, subscription]);

  const billingNextLabel = useMemo(() => {
    const sub = subscription as UserSubscription | null | undefined;
    if (isStripeSubscriptionEnded) {
      return t('subscriptions.profile.labels.subscriptionEndedOn');
    }
    if (
      sub?.stripeSubscriptionStatus === 'active' &&
      sub.stripeCancelAtPeriodEnd
    ) {
      return t('subscriptions.profile.labels.accessUntil');
    }
    if (sub?.stripeCollectionMethod === 'send_invoice') {
      return t('subscriptions.profile.labels.nextInvoice');
    }
    return nextPaymentLabel;
  }, [subscription, nextPaymentLabel, t, isStripeSubscriptionEnded]);

  const billingCyclePrimaryText = useMemo(() => {
    const sub = subscription as UserSubscription | null | undefined;
    if (!sub || !isPaidActive) return '';
    if (
      sub.stripePriceUnitAmount != null &&
      sub.stripePriceCurrency &&
      sub.stripePriceInterval
    ) {
      const per =
        sub.stripePriceInterval === 'year'
          ? t('changePlan.perYear').trim()
          : sub.stripePriceInterval === 'month'
          ? t('changePlan.perMonth').trim()
          : '';
      const cycle =
        sub.stripePriceInterval === 'year'
          ? t('subscriptions.profile.labels.paymentCycleYear')
          : sub.stripePriceInterval === 'month'
          ? t('subscriptions.profile.labels.paymentCycleMonth')
          : translatedPaymentCycle;
      return `${cycle} · ${formatMoney(
        sub.stripePriceCurrency,
        sub.stripePriceUnitAmount,
      )}${per ? ` ${per}` : ''}`;
    }
    if (isInvoiceBillingLayout) {
      return t('subscriptions.profile.labels.invoiceBillingCyclePrimary');
    }
    if (translatedPaymentCycle && sub.subscriptionPlan) {
      return `${translatedPaymentCycle} · ${formatMoney(
        sub.subscriptionPlan.currency,
        sub.frequency === SubscriptionFrequency.YEAR
          ? sub.subscriptionPlan.yearlyValue
          : sub.subscriptionPlan.monthlyValue,
      )}${
        sub.frequency === SubscriptionFrequency.YEAR
          ? ` ${t('changePlan.perYear').trim()}`
          : ` ${t('changePlan.perMonth').trim()}`
      }`;
    }
    return translatedPaymentCycle || '';
  }, [
    subscription,
    isPaidActive,
    isInvoiceBillingLayout,
    translatedPaymentCycle,
    t,
  ]);

  const invoiceContactEmail = useMemo(() => {
    const sub = subscription as UserSubscription | null | undefined;
    if (!sub) return undefined;
    if (sub.enterpriseInvoiceRequest?.contactEmail) {
      return sub.enterpriseInvoiceRequest.contactEmail;
    }
    if (Array.isArray(sub.payerEmails) && sub.payerEmails[0]) {
      return sub.payerEmails[0];
    }
    return undefined;
  }, [subscription]);

  const maxBucketSize = subscription?.maxStorage || 0;
  const maxCommunities = subscription?.maxCommunities || 0;
  const maxGroups = subscription?.maxGroups || 0;
  const maxUsers = subscription?.maxMembers || 0;

  const checkoutPlanLabel = useMemo(
    () =>
      subscription?.subscriptionPlan?.productName ||
      subscription?.subscriptionPlan?.name,
    [subscription?.subscriptionPlan],
  );

  const onboardingOutcomePlanLabel = useMemo(() => {
    if (checkoutStripeFlow !== 'onboarding' || !sessionIdFromUrl) {
      return undefined;
    }
    const sub = subscription as UserSubscription | undefined;
    const list = sub?.onboardingPurchases;
    if (!list?.length) return undefined;
    const hit = list.find(
      (p) => p.stripeCheckoutSessionId === sessionIdFromUrl,
    );
    return hit?.productName || hit?.slug || undefined;
  }, [checkoutStripeFlow, sessionIdFromUrl, subscription]);

  useEffect(() => {
    if (stripeOutcome !== 'success') return undefined;

    const runExponentialPoll = () => {
      let cancelled = false;
      const timeoutIds: number[] = [];
      const start = Date.now();
      const MAX_MS = 10000;

      const fetchOnce = () => {
        if (!cancelled) {
          dispatch(getCurrentUserSubscription.trigger());
        }
      };

      const clearAll = () => {
        while (timeoutIds.length) {
          const id = timeoutIds.pop();
          if (id != null) window.clearTimeout(id);
        }
      };

      fetchOnce();
      let nextDelayMs = 1000;

      const scheduleChain = () => {
        if (cancelled) return;
        const elapsed = Date.now() - start;
        if (elapsed >= MAX_MS) {
          setCheckoutSyncTimedOut(true);
          return;
        }
        const wait = Math.min(nextDelayMs, MAX_MS - elapsed);
        const id = window.setTimeout(() => {
          if (cancelled) return;
          fetchOnce();
          nextDelayMs *= 2;
          const el2 = Date.now() - start;
          if (el2 >= MAX_MS) {
            setCheckoutSyncTimedOut(true);
          } else {
            scheduleChain();
          }
        }, wait);
        timeoutIds.push(id);
      };

      scheduleChain();

      return () => {
        cancelled = true;
        clearAll();
      };
    };

    if (subscriptionReturnExpectsSync) {
      if (subscriptionCheckoutSynced) {
        dispatch(getCurrentUserSubscription.trigger());
        return undefined;
      }
      if (checkoutSyncTimedOut) {
        return undefined;
      }
      return runExponentialPoll();
    }

    if (checkoutStripeFlow === 'onboarding' && sessionIdFromUrl) {
      if (onboardingOutcomePlanLabel) {
        dispatch(getCurrentUserSubscription.trigger());
        return undefined;
      }
      if (checkoutSyncTimedOut) {
        return undefined;
      }
      return runExponentialPoll();
    }

    dispatch(getCurrentUserSubscription.trigger());
    const delayed = window.setTimeout(() => {
      dispatch(getCurrentUserSubscription.trigger());
    }, 2500);
    return () => {
      window.clearTimeout(delayed);
    };
  }, [
    stripeOutcome,
    checkoutStripeFlow,
    sessionIdFromUrl,
    onboardingOutcomePlanLabel,
    subscriptionReturnExpectsSync,
    subscriptionCheckoutSynced,
    checkoutSyncTimedOut,
    dispatch,
  ]);

  const showOnboardingConfirmingLoader = useMemo(
    () =>
      stripeOutcome === 'success' &&
      checkoutStripeFlow === 'onboarding' &&
      Boolean(sessionIdFromUrl) &&
      !onboardingOutcomePlanLabel &&
      !checkoutSyncTimedOut,
    [
      stripeOutcome,
      checkoutStripeFlow,
      sessionIdFromUrl,
      onboardingOutcomePlanLabel,
      checkoutSyncTimedOut,
    ],
  );

  const showSubscriptionConfirmingLoader = useMemo(
    () =>
      stripeOutcome === 'success' &&
      subscriptionReturnExpectsSync &&
      !subscriptionCheckoutSynced &&
      !checkoutSyncTimedOut,
    [
      stripeOutcome,
      subscriptionReturnExpectsSync,
      subscriptionCheckoutSynced,
      checkoutSyncTimedOut,
    ],
  );

  const showCheckoutConfirmingLoader = useMemo(
    () => showOnboardingConfirmingLoader || showSubscriptionConfirmingLoader,
    [showOnboardingConfirmingLoader, showSubscriptionConfirmingLoader],
  );

  /** Avoid full-screen loader stacking during Stripe return refetch (flicker). */
  const suppressFloatingRefetchLoader = useMemo(
    () =>
      stripeOutcome === 'success' &&
      ((checkoutStripeFlow === 'onboarding' && Boolean(sessionIdFromUrl)) ||
        subscriptionReturnExpectsSync),
    [
      stripeOutcome,
      checkoutStripeFlow,
      sessionIdFromUrl,
      subscriptionReturnExpectsSync,
    ],
  );

  const paymentTrustFooter = (
    // <FooterPaymentTrustCard>
    <FooterStripeNote>
      <IconLock size={16} />
      <span>{t('subscriptions.profile.trust.poweredBy')}</span>
      <StripeWordmark height={16} />
    </FooterStripeNote>
    // </FooterPaymentTrustCard>
  );

  const handleTrialStart = () => {
    dispatch(startTrial());
  };
  const trialInlineError = trialStartErrorKey ? t(trialStartErrorKey) : null;

  const TrialStartButton = () => (
    <BtnPrimary type="button">
      <RocketLaunchIcon width={18} height={18} aria-hidden />
      {t('subscriptions.startTrial')}
    </BtnPrimary>
  );

  const showInitialLoader = isLoading && !subscription?.id;

  const billingPortalUrl = getBillingPortalUrl(subscription ?? undefined);
  const canUseManageBilling = Boolean(billingPortalUrl) && !isTrial;
  const isSubscriptionOwner = Boolean(
    profile?.userId &&
      subscription?.userId &&
      subscription.userId === profile.userId,
  );
  const showSwitchBillingMode = Boolean(
    isPaidActive &&
      isSubscriptionOwner &&
      !isStripeSubscriptionEnded &&
      subscription?.subscriptionId?.trim() &&
      (subscription as UserSubscription).stripeCollectionMethod &&
      ['charge_automatically', 'send_invoice'].includes(
        (subscription as UserSubscription).stripeCollectionMethod as string,
      ),
  );
  const paymentMethodLine = getPaymentMethodSummary(subscription ?? undefined);
  const cardExpiry = getCardExpiryParts(subscription ?? undefined);
  const lastInvoiceMeta = getLastInvoiceMeta(subscription ?? undefined);
  const lastInvoicePdfUrl = getLastInvoicePdfUrl(subscription ?? undefined);

  const usageInfo = subscription?.info;
  const usedMembers = Math.max(usageInfo?.users ?? 0, ownedUsersCount);
  const usedCommunities = Math.max(
    usageInfo?.activeCommunities ?? usageInfo?.communities ?? 0,
    ownedCommunitiesCount,
  );
  const usedGroups = Math.max(usageInfo?.groups ?? 0, groupsCount);
  const usedStorage = usageInfo?.bucketSize ?? 0;
  const atUsageLimit =
    (maxUsers > 0 && usedMembers >= maxUsers) ||
    (maxCommunities > 0 && usedCommunities >= maxCommunities) ||
    (maxGroups > 0 && usedGroups >= maxGroups) ||
    (maxBucketSize > 0 && usedStorage >= maxBucketSize - 0.0001);

  useEffect(() => {
    const userId = profile?.userId;
    if (!userId || !isPaidActive || !atUsageLimit) return;
    if (isAppToastDismissed(userId, DISMISSED_TOAST_IDS.usageLimit)) return;

    const onDismiss = () =>
      markAppToastDismissed(userId, DISMISSED_TOAST_IDS.usageLimit);

    if (isSubscriptionOwner) {
      addAppToast(
        {
          title: t('notifications.usage_limit.title'),
          message: t('notifications.usage_limit.owner'),
          link: changePlanPath,
          ctaLabel: t('subscriptions.profile.usage.upgradeCta'),
        },
        {
          appearance: 'warning',
          autoDismiss: false,
          onDismiss,
        },
      );
      return;
    }
    addAppToast(
      {
        title: t('notifications.usage_limit.title'),
        message: t('notifications.usage_limit.admin'),
      },
      {
        appearance: 'warning',
        autoDismiss: false,
        onDismiss,
      },
    );
  }, [
    atUsageLimit,
    changePlanPath,
    isPaidActive,
    isSubscriptionOwner,
    profile?.userId,
    t,
  ]);

  const paymentColumn = useMemo(
    () => (
      <Card>
        <Headline2 style={{ marginBottom: '24px' }}>
          {t('subscriptions.profile.payment.title')}
        </Headline2>
        <PaymentBlock>
          {isInvoiceBillingLayout ? (
            <>
              <PaymentRow>
                <PaymentIconWrap>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#94a3b8"
                    aria-hidden
                  >
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                  </svg>
                </PaymentIconWrap>
                <div>
                  <PaymentLabel>
                    {t('subscriptions.profile.payment.methodLabel')}
                  </PaymentLabel>
                  <PaymentValue>
                    {t('subscriptions.profile.payment.invoiceMethodTitle')}
                  </PaymentValue>
                  {invoiceContactEmail ? (
                    <p
                      style={{
                        margin: '6px 0 0',
                        fontSize: 11,
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {t('subscriptions.profile.payment.sentToBilling', {
                        email: invoiceContactEmail,
                      })}
                    </p>
                  ) : null}
                  {showSwitchBillingMode ? (
                    <SwitchBillingModeLink
                      subscription={subscription as UserSubscription}
                    />
                  ) : null}
                </div>
              </PaymentRow>

              <PaymentRow>
                <PaymentIconWrap>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#94a3b8"
                    aria-hidden
                  >
                    <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
                  </svg>
                </PaymentIconWrap>
                <div>
                  <PaymentLabel>
                    {t('subscriptions.profile.payment.cycleLabel')}
                  </PaymentLabel>
                  <PaymentValue>{billingCyclePrimaryText}</PaymentValue>
                  <p
                    style={{
                      margin: '10px 0 0',
                      fontSize: 11,
                      color: '#64748b',
                      fontWeight: 500,
                    }}
                  >
                    {billingNextLabel}{' '}
                    <span style={{ color: '#191c1e', fontWeight: 700 }}>
                      {subscriptionEndDate}
                    </span>
                  </p>
                </div>
              </PaymentRow>
              {enterpriseAccountManagerName || enterpriseAccountManagerEmail ? (
                <PaymentRow>
                  <PaymentIconWrap>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="#94a3b8"
                      aria-hidden
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </PaymentIconWrap>
                  <div>
                    <PaymentLabel>
                      {t('subscriptions.profile.payment.accountManagerLabel')}
                    </PaymentLabel>
                    <PaymentValue>
                      {enterpriseAccountManagerName || '-'}
                    </PaymentValue>
                    {enterpriseAccountManagerEmail ? (
                      <TextLink
                        href={`mailto:${enterpriseAccountManagerEmail}`}
                        style={{
                          fontSize: 11,
                          marginTop: 6,
                          display: 'inline-block',
                        }}
                      >
                        {enterpriseAccountManagerEmail}
                      </TextLink>
                    ) : null}
                  </div>
                </PaymentRow>
              ) : null}
              <PaymentRow>
                <PaymentIconWrap>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#94a3b8"
                    aria-hidden
                  >
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                  </svg>
                </PaymentIconWrap>
                <div>
                  <PaymentLabel>
                    {t('subscriptions.profile.payment.lastInvoiceLabel')}
                  </PaymentLabel>
                  <PaymentValue style={{ fontSize: 13 }}>
                    {lastInvoiceMeta
                      ? `${formatMoney(
                          lastInvoiceMeta.currency,
                          lastInvoiceMeta.amount,
                        )} · ${formatSubscriptionDate(
                          lastInvoiceMeta.paidAt,
                          i18n.language,
                        )}`
                      : t(
                          'subscriptions.profile.payment.lastInvoiceUnavailable',
                        )}
                  </PaymentValue>
                  <p
                    style={{
                      margin: '6px 0 0',
                      fontSize: 11,
                      color: '#64748b',
                      fontWeight: 500,
                    }}
                  >
                    {t('subscriptions.profile.payment.lastInvoiceHintInvoice')}
                  </p>
                  {lastInvoicePdfUrl || billingPortalUrl ? (
                    <TextLink
                      href={lastInvoicePdfUrl || billingPortalUrl || '#'}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: 11,
                        marginTop: 4,
                        display: 'inline-block',
                      }}
                    >
                      {t('subscriptions.profile.payment.downloadInvoice')}
                    </TextLink>
                  ) : null}
                </div>
              </PaymentRow>
            </>
          ) : (
            <>
              <PaymentRow>
                {isPaidActive ? (
                  <PaymentIconWrap $card>
                    <PaymentCardBrandIcon
                      brand={
                        parseStripePaymentMethod(
                          (subscription as UserSubscription)?.stripePayment,
                        )?.card?.brand
                      }
                    />
                  </PaymentIconWrap>
                ) : (
                  <PaymentIconWrap>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="#94a3b8"
                      aria-hidden
                    >
                      <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                    </svg>
                  </PaymentIconWrap>
                )}
                <div>
                  <PaymentLabel>
                    {t('subscriptions.profile.payment.methodLabel')}
                  </PaymentLabel>
                  <PaymentValue>
                    {isPaidActive
                      ? paymentMethodLine ||
                        t('subscriptions.profile.payment.methodOnFile')
                      : t('subscriptions.profile.payment.noPaymentYet')}
                  </PaymentValue>
                  {isPaidActive && cardExpiry ? (
                    <p
                      style={{
                        margin: '6px 0 0',
                        fontSize: 11,
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {t('subscriptions.profile.payment.cardExpiresShort', {
                        month: String(cardExpiry.month).padStart(2, '0'),
                        year: String(cardExpiry.year),
                      })}
                    </p>
                  ) : null}
                  {showSwitchBillingMode ? (
                    <SwitchBillingModeLink
                      subscription={subscription as UserSubscription}
                    />
                  ) : null}
                </div>
              </PaymentRow>

              <PaymentRow>
                <PaymentIconWrap>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="#94a3b8"
                    aria-hidden
                  >
                    <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
                  </svg>
                </PaymentIconWrap>
                <div>
                  <PaymentLabel>
                    {t('subscriptions.profile.payment.cycleLabel')}
                  </PaymentLabel>
                  <PaymentValue>
                    {isPaidActive && billingCyclePrimaryText
                      ? billingCyclePrimaryText
                      : isPaidActive && translatedPaymentCycle
                      ? translatedPaymentCycle
                      : t('subscriptions.profile.payment.noActiveSubscription')}
                  </PaymentValue>
                  {isPaidActive ? (
                    <p
                      style={{
                        margin: '10px 0 0',
                        fontSize: 11,
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {billingNextLabel}{' '}
                      <span style={{ color: '#191c1e', fontWeight: 700 }}>
                        {subscriptionEndDate}
                      </span>
                    </p>
                  ) : null}
                </div>
              </PaymentRow>
              {isPaidActive ? (
                <PaymentRow>
                  <PaymentIconWrap>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="#94a3b8"
                      aria-hidden
                    >
                      <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                    </svg>
                  </PaymentIconWrap>
                  <div>
                    <PaymentLabel>
                      {t('subscriptions.profile.payment.lastInvoiceLabel')}
                    </PaymentLabel>
                    <PaymentValue style={{ fontSize: 13 }}>
                      {lastInvoiceMeta
                        ? `${formatMoney(
                            lastInvoiceMeta.currency,
                            lastInvoiceMeta.amount,
                          )} · ${formatSubscriptionDate(
                            lastInvoiceMeta.paidAt,
                            i18n.language,
                          )}`
                        : t(
                            'subscriptions.profile.payment.lastInvoiceUnavailable',
                          )}
                    </PaymentValue>
                    <p
                      style={{
                        margin: '6px 0 0',
                        fontSize: 11,
                        color: '#64748b',
                        fontWeight: 500,
                      }}
                    >
                      {t('subscriptions.profile.payment.lastInvoiceHint')}
                    </p>
                    {lastInvoicePdfUrl || billingPortalUrl ? (
                      <TextLink
                        href={lastInvoicePdfUrl || billingPortalUrl || '#'}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          fontSize: 11,
                          marginTop: 4,
                          display: 'inline-block',
                        }}
                      >
                        {t('subscriptions.profile.payment.downloadInvoice')}
                      </TextLink>
                    ) : null}
                  </div>
                </PaymentRow>
              ) : null}
            </>
          )}
        </PaymentBlock>
        <div
          style={{
            marginTop: 'auto',
            paddingTop: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {canUseManageBilling ? (
            <BtnOutline
              href={billingPortalUrl}
              target="_blank"
              rel="noreferrer"
            >
              <IconOpen />
              {t('subscriptions.profile.payment.manageBilling')}
            </BtnOutline>
          ) : (
            <BtnOutline as="button" type="button" disabled aria-disabled="true">
              <IconOpen />
              {t('subscriptions.profile.payment.manageBilling')}
            </BtnOutline>
          )}
          {/* <SmallHint style={{ marginTop: 0 }}>
            <IconLock size={13} />
            {t('subscriptions.profile.payment.manageBillingHint')}
          </SmallHint> */}
        </div>
      </Card>
    ),
    [
      billingCyclePrimaryText,
      billingNextLabel,
      billingPortalUrl,
      canUseManageBilling,
      cardExpiry,
      i18n.language,
      invoiceContactEmail,
      isInvoiceBillingLayout,
      isPaidActive,
      isTrial,
      lastInvoiceMeta,
      showSwitchBillingMode,
      subscription,
      lastInvoicePdfUrl,
      paymentMethodLine,
      subscription,
      subscriptionEndDate,
      t,
      translatedPaymentCycle,
    ],
  );

  const usageSection = (locked: boolean, showUsageGuidance = false) => {
    const m = locked ? 0 : maxUsers;
    const c = locked ? 0 : maxCommunities;
    const g = locked ? 0 : maxGroups;
    const s = locked ? 0 : maxBucketSize;
    const usedMembersInCard = locked ? 0 : usedMembers;
    const usedCommunitiesInCard = locked ? 0 : usedCommunities;
    const usedGroupsInCard = locked ? 0 : usedGroups;
    const usedStorageInCard = locked ? 0 : usedStorage;

    const membersAtLimit =
      showUsageGuidance && !locked && m > 0 && usedMembersInCard >= m;
    const commAtLimit =
      showUsageGuidance && !locked && c > 0 && usedCommunitiesInCard >= c;
    const schedAtLimit =
      showUsageGuidance && !locked && g > 0 && usedGroupsInCard >= g;
    const storageAtLimit =
      showUsageGuidance && !locked && s > 0 && usedStorageInCard >= s - 0.0001;
    const anyAtLimit =
      membersAtLimit || commAtLimit || schedAtLimit || storageAtLimit;

    const membersHigh =
      showUsageGuidance &&
      m > 0 &&
      pct(usedMembersInCard, m) >= 70 &&
      !membersAtLimit;

    return (
      <UsageCard>
        <UsageHeader>
          <Headline2 style={{ margin: 0 }}>
            {t('subscriptions.profile.usage.title')}
          </Headline2>
          {locked && (
            <UsageHint>
              <IconLock size={14} />
              {t('subscriptions.profile.usage.lockedHint')}
            </UsageHint>
          )}
        </UsageHeader>
        <UsageGrid $dimmed={locked}>
          <UsageMetric>
            <UsageMetricTop>
              <UsageLabel>
                {t('subscriptions.profile.usage.members')}
              </UsageLabel>
              {locked ? (
                <UsageFractionMuted>0 / -</UsageFractionMuted>
              ) : (
                <UsageFraction
                  $tone={
                    membersAtLimit ? 'amber' : membersHigh ? 'amber' : 'default'
                  }
                >
                  {usedMembersInCard} / {m || '-'}
                </UsageFraction>
              )}
            </UsageMetricTop>
            <BarTrack>
              {!locked && m > 0 ? (
                <BarFill
                  $pct={pct(usedMembersInCard, m)}
                  $tone={
                    membersAtLimit ? 'amber' : membersHigh ? 'amber' : 'default'
                  }
                />
              ) : null}
            </BarTrack>
            {showUsageGuidance && !locked && m > 0 ? (
              membersAtLimit ? (
                <UsageHintWarn>
                  {t('subscriptions.profile.usage.atLimitMembers')}
                </UsageHintWarn>
              ) : (
                <UsageHintSmall>
                  {t('subscriptions.profile.usage.spotsRemaining', {
                    count: Math.max(0, m - usedMembersInCard),
                  })}
                </UsageHintSmall>
              )
            ) : null}
          </UsageMetric>
          <UsageMetric>
            <UsageMetricTop>
              <UsageLabel>
                {t('subscriptions.profile.usage.communities')}
              </UsageLabel>
              {locked ? (
                <UsageFractionMuted>0 / -</UsageFractionMuted>
              ) : (
                <UsageFraction $tone={commAtLimit ? 'amber' : 'default'}>
                  {usedCommunitiesInCard} / {c || '-'}
                </UsageFraction>
              )}
            </UsageMetricTop>
            <BarTrack>
              {!locked && c > 0 ? (
                <BarFill
                  $pct={pct(usedCommunitiesInCard, c)}
                  $tone={commAtLimit ? 'amber' : 'default'}
                />
              ) : null}
            </BarTrack>
            {showUsageGuidance && !locked && c > 0 ? (
              commAtLimit ? (
                <UsageHintWarn>
                  {t('subscriptions.profile.usage.atLimitCommunities')}
                </UsageHintWarn>
              ) : (
                <UsageHintSmall>
                  {t('subscriptions.profile.usage.communitiesRemaining', {
                    count: Math.max(0, c - usedCommunitiesInCard),
                  })}
                </UsageHintSmall>
              )
            ) : null}
          </UsageMetric>
          <UsageMetric>
            <UsageMetricTop>
              <UsageLabel>
                {t('subscriptions.profile.usage.schedulers')}
              </UsageLabel>
              {locked ? (
                <UsageFractionMuted>0 / -</UsageFractionMuted>
              ) : (
                <UsageFraction $tone={schedAtLimit ? 'amber' : 'default'}>
                  {usedGroupsInCard} / {g || '-'}
                </UsageFraction>
              )}
            </UsageMetricTop>
            <BarTrack>
              {!locked && g > 0 ? (
                <BarFill
                  $pct={pct(usedGroupsInCard, g)}
                  $tone={schedAtLimit ? 'amber' : 'default'}
                />
              ) : null}
            </BarTrack>
            {showUsageGuidance && !locked && g > 0 ? (
              schedAtLimit ? (
                <UsageHintWarn>
                  {t('subscriptions.profile.usage.atLimitSchedulers')}
                </UsageHintWarn>
              ) : (
                <UsageHintSmall>
                  {t('subscriptions.profile.usage.schedulersRemaining', {
                    count: Math.max(0, g - usedGroupsInCard),
                  })}
                </UsageHintSmall>
              )
            ) : null}
          </UsageMetric>
          <UsageMetric>
            <UsageMetricTop>
              <UsageLabel>
                {t('subscriptions.profile.usage.storage')}
              </UsageLabel>
              {locked ? (
                <UsageFractionMuted>0.00 GB / -</UsageFractionMuted>
              ) : (
                <UsageFraction $tone={storageAtLimit ? 'amber' : 'default'}>
                  {usedStorageInCard.toFixed(2)} GB / {s > 0 ? `${s} GB` : '-'}
                </UsageFraction>
              )}
            </UsageMetricTop>
            <BarTrack>
              {!locked && s > 0 ? (
                <BarFill
                  $pct={pct(usedStorageInCard, s)}
                  $tone={storageAtLimit ? 'amber' : 'default'}
                />
              ) : null}
            </BarTrack>
            {showUsageGuidance && !locked && s > 0 ? (
              storageAtLimit ? (
                <UsageHintWarn>
                  {t('subscriptions.profile.usage.atLimitStorage')}
                </UsageHintWarn>
              ) : (
                <UsageHintSmall>
                  {t('subscriptions.profile.usage.storageRemaining', {
                    count: Math.max(
                      0,
                      Math.round((s - usedStorageInCard) * 10) / 10,
                    ),
                  })}
                </UsageHintSmall>
              )
            ) : null}
          </UsageMetric>
        </UsageGrid>
        {anyAtLimit ? (
          <UsageUpgradeNudge>
            <div style={{ flexShrink: 0, marginTop: 2 }}>
              <IconInfo />
            </div>
            <UsageUpgradeNudgeText>
              <strong>{t('notifications.usage_limit.title')}</strong>
              <span style={{ display: 'block', marginTop: 4 }}>
                {isSubscriptionOwner
                  ? t('notifications.usage_limit.owner')
                  : t('notifications.usage_limit.admin')}
              </span>
            </UsageUpgradeNudgeText>
            {isSubscriptionOwner ? (
              <UsageUpgradeBtn as={Link} to={changePlanPath}>
                {t('subscriptions.profile.usage.upgradeCta')}
              </UsageUpgradeBtn>
            ) : null}
          </UsageUpgradeNudge>
        ) : null}
      </UsageCard>
    );
  };

  const freeAccountBody = () => (
    <>
      <HeroCard>
        <HeroAccent />
        <HeroInner>
          <HeroLeft>
            <IconBox $filled>
              <RocketLaunchIcon width={22} height={22} aria-hidden />
            </IconBox>
            <div>
              <HeroTitle>{t('subscriptions.profile.hero.title')}</HeroTitle>
              <HeroSubtitle>
                {t('subscriptions.profile.hero.subtitle')}
              </HeroSubtitle>
            </div>
          </HeroLeft>
          <HeroActions>
            <TextLink as={Link} to={changePlanPath}>
              {t('subscriptions.profile.hero.seeIncluded')}
            </TextLink>
            {!profile?.trialStarted ? (
              <>
                <PopupButtonMessage
                  popupHeaderText={t('subscriptions.startTrialHeader')}
                  popupMessageText={t('subscriptions.startTrialText')}
                  popupButtonText={t('subscriptions.startTrialPopupButton')}
                  ButtonComponent={TrialStartButton}
                  onConfirm={handleTrialStart}
                />
                {trialInlineError ? (
                  <InfoNote role="alert">{trialInlineError}</InfoNote>
                ) : null}
              </>
            ) : null}
          </HeroActions>
        </HeroInner>
      </HeroCard>

      <TwoColGrid>
        <Card>
          <CardHeaderRow>
            <div>
              <Headline>{t('subscriptions.profile.plan.title')}</Headline>
              <CardSubtitle>
                {t('subscriptions.profile.freeAccount.subtitle')}
              </CardSubtitle>
            </div>
            <Badge $tone="slate">
              {t('subscriptions.profile.freeAccount.badge')}
            </Badge>
          </CardHeaderRow>
          <PlanName $muted>
            {t('subscriptions.profile.freeAccount.noActivePlan')}
          </PlanName>
          <FeatureBox>
            <FeatureBlurb>
              {t('subscriptions.profile.freeAccount.trialUnlockBlurb')}
            </FeatureBlurb>
            <FeatureList>
              <FeatureItem>
                <IconCheck />
                {t('subscriptions.profile.plan.featureCommunities', {
                  count: TRIAL_DEFAULTS.maxCommunities,
                })}
              </FeatureItem>
              <FeatureItem>
                <IconCheck />
                {t('subscriptions.profile.plan.featureSchedulers', {
                  count: TRIAL_DEFAULTS.maxGroups,
                })}
              </FeatureItem>
              <FeatureItem>
                <IconCheck />
                {t('subscriptions.profile.plan.featureMembers', {
                  count: TRIAL_DEFAULTS.maxMembers,
                })}
              </FeatureItem>
              <FeatureItem>
                <IconCheck />
                {t('subscriptions.profile.plan.featureStorage', {
                  count: TRIAL_DEFAULTS.maxStorage,
                })}
              </FeatureItem>
            </FeatureList>
          </FeatureBox>
          <PlanFooter>
            <FooterNote>
              <IconCalendar />
              {t('subscriptions.profile.freeAccount.trialNoteFooter')}
            </FooterNote>
            <ActionRow>
              {!profile?.trialStarted ? (
                <>
                  <PopupButtonMessage
                    popupHeaderText={t('subscriptions.startTrialHeader')}
                    popupMessageText={t('subscriptions.startTrialText')}
                    popupButtonText={t('subscriptions.startTrialPopupButton')}
                    ButtonComponent={TrialStartButton}
                    onConfirm={handleTrialStart}
                  />
                  {trialInlineError ? (
                    <InfoNote role="alert">{trialInlineError}</InfoNote>
                  ) : null}
                </>
              ) : null}
              <TextLink as={Link} to={changePlanPath}>
                {t('subscriptions.profile.freeAccount.comparePlans')}
              </TextLink>
            </ActionRow>
          </PlanFooter>
        </Card>
        {paymentColumn}
      </TwoColGrid>

      {usageSection(true)}

      <TrustRow>{paymentTrustFooter}</TrustRow>
    </>
  );

  const subscribedBody = () => {
    if (!subscription?.id) return null;

    const paidProExtras =
      isPaidActive && !isTrial
        ? (
            t('subscriptions.profile.plan.paidProFeatureExtras', {
              returnObjects: true,
            }) as string[]
          ).filter(Boolean)
        : [];

    const featureItems = (
      <FeatureList style={{ marginBottom: '28px' }}>
        <FeatureItem>
          <IconCheck />
          {t('subscriptions.profile.plan.featureCommunities', {
            count: maxCommunities || TRIAL_DEFAULTS.maxCommunities,
          })}
        </FeatureItem>
        <FeatureItem>
          <IconCheck />
          {t('subscriptions.profile.plan.featureSchedulers', {
            count: maxGroups || TRIAL_DEFAULTS.maxGroups,
          })}
        </FeatureItem>
        <FeatureItem>
          <IconCheck />
          {t('subscriptions.profile.plan.featureMembers', {
            count: maxUsers || TRIAL_DEFAULTS.maxMembers,
          })}
        </FeatureItem>
        <FeatureItem>
          <IconCheck />
          {t('subscriptions.profile.plan.featureStorage', {
            count: maxBucketSize || TRIAL_DEFAULTS.maxStorage,
          })}
        </FeatureItem>
        {paidProExtras.map((line) => (
          <FeatureItem key={line}>
            <IconCheck />
            {line}
          </FeatureItem>
        ))}
      </FeatureList>
    );

    let topBanner: React.ReactNode = null;
    if (isTrial) {
      topBanner = (
        <TrialBanner>
          <TrialBannerWarningLottie />
          <TrialBannerContent>
            <TrialBannerTitle>
              {t('subscriptions.profile.statusBanner.trialTitle')}
            </TrialBannerTitle>
            <TrialBannerText>
              {t('subscriptions.profile.statusBanner.trialBody', {
                date: subscriptionEndDate,
              })}
            </TrialBannerText>
          </TrialBannerContent>
        </TrialBanner>
      );
    } else if (isPaidActive && isStripeSubscriptionEnded) {
      topBanner = (
        <StatusBanner $variant="muted">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ marginBottom: 4 }}>
              <StatusBannerTitle>
                {planName} · {t('subscriptions.profile.plan.badgeEnded')}
              </StatusBannerTitle>
            </div>
            <StatusBannerBody
              as="div"
              style={{ marginLeft: 0, display: 'block' }}
            >
              {t('subscriptions.profile.statusBanner.stripeEndedRich')}
            </StatusBannerBody>
          </div>
          <Badge $tone="slate">
            {t('subscriptions.profile.plan.badgeEnded')}
          </Badge>
        </StatusBanner>
      );
    } else if (isPaidActive && isStripeNonRenewingActive) {
      const sp = subscription.subscriptionPlan;
      const cur = sp?.currency || 'USD';
      const renewalAmt =
        subscription.frequency === SubscriptionFrequency.YEAR
          ? sp?.yearlyValue ?? 0
          : sp?.monthlyValue ?? 0;
      const periodForBanner =
        subscription.frequency === SubscriptionFrequency.YEAR
          ? t('changePlan.perYear')
          : t('changePlan.perMonth');
      topBanner = (
        <StatusBanner $variant="success">
          <StatusBannerMain>
            <StatusCheckWrap>
              <IconCheck />
            </StatusCheckWrap>
            <StatusBannerTextCol>
              <StatusBannerTitle as="div" style={{ display: 'block' }}>
                {t('subscriptions.profile.statusBanner.activeLabel', {
                  plan: planName,
                })}
              </StatusBannerTitle>
              <StatusBannerBody
                as="div"
                style={{ marginLeft: 0, display: 'block', marginTop: 4 }}
              >
                {t('subscriptions.profile.statusBanner.nonRenewalRich', {
                  date: subscriptionEndDate,
                  price: formatMoney(cur, renewalAmt),
                  period: periodForBanner,
                })}
              </StatusBannerBody>
            </StatusBannerTextCol>
          </StatusBannerMain>
          <FrequencyPill>
            {subscription.frequency === SubscriptionFrequency.YEAR
              ? t('subscriptions.profile.statusBanner.frequencyAnnual')
              : t('subscriptions.profile.statusBanner.frequencyMonthly')}
          </FrequencyPill>
        </StatusBanner>
      );
    } else if (isPaidActive) {
      const sp = subscription.subscriptionPlan;
      const cur = sp?.currency || 'USD';
      const renewalAmt =
        subscription.frequency === SubscriptionFrequency.YEAR
          ? sp?.yearlyValue ?? 0
          : sp?.monthlyValue ?? 0;
      const periodForBanner =
        subscription.frequency === SubscriptionFrequency.YEAR
          ? t('changePlan.perYear')
          : t('changePlan.perMonth');
      topBanner = (
        <StatusBanner $variant="success">
          <StatusBannerMain>
            <StatusCheckWrap>
              <IconCheck />
            </StatusCheckWrap>
            <StatusBannerTextCol>
              <StatusBannerTitle as="div" style={{ display: 'block' }}>
                {t('subscriptions.profile.statusBanner.activeLabel', {
                  plan: planName,
                })}
              </StatusBannerTitle>
              <StatusBannerBody
                as="div"
                style={{ marginLeft: 0, display: 'block', marginTop: 4 }}
              >
                {t('subscriptions.profile.statusBanner.renewalRich', {
                  date: subscriptionEndDate,
                  price: formatMoney(cur, renewalAmt),
                  period: periodForBanner,
                })}
              </StatusBannerBody>
            </StatusBannerTextCol>
          </StatusBannerMain>
          <FrequencyPill>
            {subscription.frequency === SubscriptionFrequency.YEAR
              ? t('subscriptions.profile.statusBanner.frequencyAnnual')
              : t('subscriptions.profile.statusBanner.frequencyMonthly')}
          </FrequencyPill>
        </StatusBanner>
      );
    } else if (isCancelled) {
      topBanner = (
        <StatusBanner $variant="muted">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ marginBottom: 4 }}>
              <StatusBannerTitle>
                {t('subscriptions.cancelledTitle')}
              </StatusBannerTitle>
            </div>
            <StatusBannerBody
              as="div"
              style={{ marginLeft: 0, display: 'block' }}
            >
              {t('subscriptions.profile.labels.infoCancelled')}
            </StatusBannerBody>
          </div>
          <Badge $tone="slate">
            {t('subscriptions.profile.plan.badgeCancelled')}
          </Badge>
        </StatusBanner>
      );
    }

    const planSubtitle = isTrial
      ? t('subscriptions.profile.plan.subtitleTrial')
      : isCancelled
      ? `${t('subscriptions.profile.labels.validUntil')} ${subscriptionEndDate}`
      : isPaidActive && isStripeSubscriptionEnded
      ? t('subscriptions.profile.plan.subtitleStripeEnded', {
          date: subscriptionEndDate,
        })
      : isPaidActive && isStripeNonRenewingActive
      ? t('subscriptions.profile.plan.subtitleNonRenewal', {
          date: subscriptionEndDate,
        })
      : t('subscriptions.profile.plan.subtitleActive');

    const badge = isTrial ? (
      <Badge $tone="teal">{t('subscriptions.profile.plan.badgeTrial')}</Badge>
    ) : isCancelled ? (
      <Badge $tone="slate">
        {t('subscriptions.profile.plan.badgeCancelled')}
      </Badge>
    ) : isPaidActive && isStripeSubscriptionEnded ? (
      <Badge $tone="slate">
        {planName} · {t('subscriptions.profile.plan.badgeEnded')}
      </Badge>
    ) : isPaidActive ? (
      <Badge $tone="teal">
        {planName} · {t('subscriptions.profile.plan.badgeActive')}
      </Badge>
    ) : (
      <Badge $tone="teal">{t('subscriptions.profile.plan.badgeActive')}</Badge>
    );

    const footerNote = isTrial ? (
      <FooterNote>
        <IconCalendar />
        {t('subscriptions.profile.plan.endsOnNote', {
          date: subscriptionEndDate,
        })}
      </FooterNote>
    ) : isPaidActive && subscription.subscriptionPlan ? (
      <FooterNote>
        <IconCalendar />
        {isStripeSubscriptionEnded
          ? t('subscriptions.profile.plan.footnoteStripeEnded', {
              date: subscriptionEndDate,
            })
          : isStripeNonRenewingActive
          ? t('subscriptions.profile.plan.footnoteRenewalCancelled', {
              date: subscriptionEndDate,
              price: formatMoney(
                subscription.subscriptionPlan.currency,
                subscription.frequency === SubscriptionFrequency.YEAR
                  ? subscription.subscriptionPlan.yearlyValue
                  : subscription.subscriptionPlan.monthlyValue,
              ),
              period:
                subscription.frequency === SubscriptionFrequency.YEAR
                  ? t('changePlan.perYear')
                  : t('changePlan.perMonth'),
              chargedTo:
                paymentMethodLine ||
                t('subscriptions.profile.plan.paymentMethodChargedGeneric'),
            })
          : t('subscriptions.profile.plan.footnoteRenewalPayment', {
              date: subscriptionEndDate,
              price: formatMoney(
                subscription.subscriptionPlan.currency,
                subscription.frequency === SubscriptionFrequency.YEAR
                  ? subscription.subscriptionPlan.yearlyValue
                  : subscription.subscriptionPlan.monthlyValue,
              ),
              period:
                subscription.frequency === SubscriptionFrequency.YEAR
                  ? t('changePlan.perYear')
                  : t('changePlan.perMonth'),
              chargedTo:
                paymentMethodLine ||
                t('subscriptions.profile.plan.paymentMethodChargedGeneric'),
            })}
      </FooterNote>
    ) : (
      <FooterNote>
        <IconCalendar />
        {nextPaymentLabel} {subscriptionEndDate}
      </FooterNote>
    );

    const planActions = (() => {
      if (isCancelled) {
        return (
          <PopupEventWrapper
            popupMessageText={t('subscriptions.renewSubscriptionText')}
            popupButtons={[
              {
                popupButtonText: t('subscriptions.activate'),
                handleConfirm: () => {
                  if (subscription.subscriptionId) {
                    dispatch(activateSubscription(subscription.subscriptionId));
                  }
                },
              },
            ]}
            darkBackground
          >
            <BtnPrimary type="button">
              {t('subscriptions.profile.actions.restartMembership')}
            </BtnPrimary>
          </PopupEventWrapper>
        );
      }
      if (isTrial) {
        return (
          <ActionRow>
            <ChoosePlanFullButton as={Link} to={changePlanPath}>
              {t('subscriptions.profile.actions.choosePlan')}
              <IconChevronRight />
            </ChoosePlanFullButton>
            <TextActionLink as={Link} to={cancelSubscriptionPath}>
              {t('subscriptions.profile.actions.cancelSubscription')}
            </TextActionLink>
          </ActionRow>
        );
      }
      return (
        <ActionRow>
          <SplitButtonWrap ref={planMenuRef}>
            <SplitButtonMain as={Link} to={changePlanPath}>
              {t('subscriptions.profile.actions.changePlan')}
            </SplitButtonMain>
            <SplitButtonChevron
              type="button"
              aria-expanded={planMenuOpen}
              aria-label="Plan options"
              onClick={(e) => {
                e.stopPropagation();
                setPlanMenuOpen((o) => !o);
              }}
            >
              <IconChevronDown />
            </SplitButtonChevron>
            <DropdownPanel $open={planMenuOpen}>
              <DropdownItem
                as={Link}
                to={changePlanPath}
                onClick={() => setPlanMenuOpen(false)}
              >
                {t('subscriptions.profile.actions.choosePlan')}
              </DropdownItem>
              <DropdownItem
                as={Link}
                to={changePlanPath}
                onClick={() => setPlanMenuOpen(false)}
              >
                {t('subscriptions.profile.actions.comparePlansMenu')}
              </DropdownItem>
            </DropdownPanel>
          </SplitButtonWrap>
          {isPaidActive && !isTrial && !isStripeSubscriptionEnded ? (
            isStripeNonRenewingActive ? (
              billingPortalUrl ? (
                <TextActionLink
                  href={billingPortalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('subscriptions.profile.actions.dontCancelSubscription')}
                </TextActionLink>
              ) : null
            ) : (
              <TextActionLink as={Link} to={cancelSubscriptionPath}>
                {t('subscriptions.profile.actions.cancelSubscription')}
              </TextActionLink>
            )
          ) : null}
        </ActionRow>
      );
    })();

    return (
      <>
        {topBanner}
        <TwoColGrid>
          <Card>
            <CardHeaderRow>
              <div>
                <Headline2>{t('subscriptions.profile.plan.title')}</Headline2>
                <CardSubtitle>{planSubtitle}</CardSubtitle>
              </div>
              {badge}
            </CardHeaderRow>
            <PlanName $muted={isCancelled}>{planName}</PlanName>
            {featureItems}
            <PlanFooter>
              {footerNote}
              {planActions}
            </PlanFooter>
          </Card>
          {paymentColumn}
        </TwoColGrid>
        {usageSection(false, true)}
        <TrustRow>{paymentTrustFooter}</TrustRow>
      </>
    );
  };

  return (
    <ContentWrapper minHeight="100svh">
      <PageRoot>
        {isLoading && subscription?.id && !suppressFloatingRefetchLoader && (
          <FloatingLoaderWrap>
            <Loader size="60px" type={null} />
          </FloatingLoaderWrap>
        )}

        <Layout.Header>
          <Headline>{t('subscriptions.profile.page.title')}</Headline>
        </Layout.Header>
        <Main>
          {stripeOutcome ? (
            showCheckoutConfirmingLoader ? (
              <OnboardingConfirmCard>
                <OnboardingConfirmLoaderSlot>
                  <Loader size="56px" type={null} />
                </OnboardingConfirmLoaderSlot>
                <OnboardingConfirmTitle>
                  {checkoutStripeFlow === 'onboarding'
                    ? t('subscriptions.profile.confirming.onboardingTitle')
                    : t('subscriptions.profile.confirming.subscriptionTitle')}
                </OnboardingConfirmTitle>
                <OnboardingConfirmHint>
                  {checkoutStripeFlow === 'onboarding'
                    ? t('subscriptions.profile.confirming.onboardingHint')
                    : t('subscriptions.profile.confirming.subscriptionHint')}
                </OnboardingConfirmHint>
                <OnboardingConfirmShimmer aria-hidden />
                <OnboardingConfirmShimmerShort aria-hidden />
              </OnboardingConfirmCard>
            ) : (
              <CheckoutOutcomeEnter
                key={
                  checkoutStripeFlow === 'onboarding' && sessionIdFromUrl
                    ? `onb-${sessionIdFromUrl}`
                    : checkoutStripeFlow === 'subscription' &&
                      sessionIdFromUrl &&
                      subscriptionReturnExpectsSync
                    ? `sub-${sessionIdFromUrl}`
                    : 'stripe-outcome'
                }
              >
                <StripeCheckoutOutcome
                  variant={stripeOutcome}
                  sessionId={sessionIdFromUrl}
                  planLabel={
                    checkoutStripeFlow === 'onboarding'
                      ? onboardingOutcomePlanLabel
                      : checkoutPlanLabel
                  }
                  checkoutFlow={checkoutStripeFlow}
                  onDismiss={clearStripeQuery}
                />
              </CheckoutOutcomeEnter>
            )
          ) : showInitialLoader ? (
            <Loader />
          ) : !subscription?.id ? (
            freeAccountBody()
          ) : (
            subscribedBody()
          )}
        </Main>
      </PageRoot>
    </ContentWrapper>
  );
};

export default UserProfileSubscription;
